/*
 * Public QR verification page for IPD Surgical Discharge Summaries.
 * Route: /surgical-verify/:viewToken — no login. Shows authenticity
 * card only (no clinical content).
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ShieldCheck, ShieldOff, Loader2 } from 'lucide-react';
import api from '@/lib/api';

const NAVY = '#07122f';
const TEAL = '#007f73';

export default function SurgicalVerifyPage() {
  const { viewToken } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/surgical-discharge/verify/${viewToken}`);
        setData(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || 'Document not found');
      }
    })();
  }, [viewToken]);

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white rounded-2xl border-2 p-8 text-center" style={{ borderColor: data ? TEAL : '#cbd5e1' }}>
        {!data && !err && <Loader2 className="animate-spin mx-auto text-slate-400" size={28} />}
        {err && (
          <>
            <ShieldOff size={36} className="mx-auto text-rose-500 mb-3" />
            <div className="text-lg font-bold" style={{ color: NAVY }}>Verification failed</div>
            <div className="text-sm text-slate-500 mt-1">{err}</div>
          </>
        )}
        {data && (
          <>
            <ShieldCheck size={36} className="mx-auto mb-3" style={{ color: TEAL }} />
            <div className="text-lg font-black" style={{ color: NAVY }}>Authentic Discharge Summary</div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 mt-1 mb-4">
              {data.status === 'sealed' ? 'Sealed & locked' : 'Draft — not yet sealed'}
            </div>
            <div className="text-left text-sm space-y-2 border-t border-slate-200 pt-4">
              <Row k="Patient" v={data.patient_name} />
              <Row k="Hospital" v={data.hospital_name} />
              <Row k="Department" v={data.department} />
              <Row k="Principal Surgeon" v={data.principal_surgeon_name} />
              <Row k="Prepared by" v={`${data.created_by_operator_name || '—'} (${(data.created_by_operator_role || 'principal').replace('_', ' ')})`} />
              <Row k="Created" v={String(data.created_at || '').slice(0, 16).replace('T', ' ')} />
              {data.sealed_at && <Row k="Sealed" v={String(data.sealed_at).slice(0, 16).replace('T', ' ')} />}
            </div>
          </>
        )}
        <Link to="/" className="mt-6 inline-block text-[12px] font-semibold" style={{ color: TEAL }}>DocSite · Surgical Suite</Link>
      </div>
    </div>
  );
}

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-4"><span className="text-slate-500 text-[12px]">{k}</span><span className="font-semibold text-right" style={{ color: NAVY }}>{v || '—'}</span></div>
);
